/**
 * Frontend → backend debug log shipper for the unified scrape-flow trace.
 *
 * Pairs with `bookc-backend/clinics/services/scrape_debug.py`. Every event
 * sent here lands in `bookc-backend/logs/scrape_flow.log` alongside the
 * backend's own scrape_debug() output, so a single `tail -F scrape_flow.log`
 * shows FE+BE interleaved.
 *
 * Hard kill-switch:
 * - The module starts with `debugEnabled = false`.
 * - On boot, `bootRuntimeFlags()` calls GET /clinics/api/runtime-flags/. If
 *   the response says `debug: true`, the buffer + 1s flush interval start.
 * - When `debugEnabled === false`, every public method short-circuits at
 *   the top — no console.debug, no buffering, no network. Zero overhead.
 *
 * The Redux logging middleware reads `isDebugEnabled()` for the same
 * decision, so flipping the server-side flag turns everything off in unison.
 */

import config from "../config";

const FLUSH_INTERVAL_MS = 1000;
const MAX_BUFFER = 200;
const MAX_BATCH = 50;

let debugEnabled = false;
let flushTimer = null;
let buffer = [];
let droppedSinceLastWarn = 0;

function authToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed && (parsed.key || parsed.token)) || "";
  } catch (e) {
    return "";
  }
}

function fetchRuntimeFlags() {
  const url = `${config.apiGateway.URL}/clinics/api/runtime-flags/`;
  const token = authToken();
  if (!token) return Promise.resolve(null);
  return fetch(url, {
    method: "GET",
    headers: { Authorization: `Token ${token}` },
  })
    .then(function (resp) { return resp.ok ? resp.json() : null; })
    .catch(function () { return null; });
}

function startFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

function stopFlushTimer() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

async function flush() {
  if (!debugEnabled) return;
  if (buffer.length === 0) return;
  const events = buffer.splice(0, MAX_BATCH);
  const url = `${config.apiGateway.URL}/clinics/api/debug-log/`;
  const token = authToken();
  if (!token) return;
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify({ events }),
    });
  } catch (e) {
    // Swallow — debug logs are not telemetry. The next button click will
    // emit fresh events.
  }
}

function enqueue(event) {
  if (buffer.length >= MAX_BUFFER) {
    droppedSinceLastWarn += 1;
    return;
  }
  if (droppedSinceLastWarn > 0) {
    buffer.push({
      level: "warning",
      phase: "fe_buffer_dropped",
      extra: { count: droppedSinceLastWarn },
    });
    droppedSinceLastWarn = 0;
  }
  buffer.push(event);
}

function emit(level, category, message, data) {
  if (!debugEnabled) return;
  // Always write to console.debug too — keeps the browser inspector
  // useful as a second view onto the same stream.
  try {
    if (typeof console !== "undefined" && console.debug) {
      console.debug(`[scrape:${category}]`, message, data || "");
    }
  } catch (e) { /* noop */ }

  // Normalize. The server treats `phase` as required; we use `category`
  // (e.g. "ws", "redux", "button") so the log line is readable.
  const event = {
    level,
    phase: typeof category === "string" ? category : String(category || "fe_event"),
    message: message ? String(message).slice(0, 400) : "",
    job_id: (data && data.job_id) || undefined,
    dispatch_id: (data && data.dispatch_id) || undefined,
    extra: {},
  };
  if (data && typeof data === "object") {
    for (const k in data) {
      if (k === "job_id" || k === "dispatch_id") continue;
      event.extra[k] = data[k];
    }
  }
  enqueue(event);
}


export const debugLog = {
  info: function (category, message, data) { emit("info", category, message, data); },
  warn: function (category, message, data) { emit("warning", category, message, data); },
  error: function (category, message, data) { emit("error", category, message, data); },
  flush: flush,
};

export function isDebugEnabled() { return !!debugEnabled; }

export function bootRuntimeFlags() {
  return fetchRuntimeFlags().then(function (flags) {
    if (flags && flags.debug) {
      debugEnabled = true;
      startFlushTimer();
      // Emit a single boot marker so the user sees confirmation in the log
      // file that frontend events are flowing.
      emit("info", "fe_boot", "frontend debug logging active", {
        scrape_jobs_enabled: !!flags.scrape_jobs_enabled,
      });
    } else {
      debugEnabled = false;
      stopFlushTimer();
    }
    return flags;
  });
}

export default debugLog;
